/**
 * Page-level SEO helper for the SPA.
 *
 * The server already injects route-specific <title>, meta, Open Graph and
 * JSON-LD on the initial HTML response (see `server/src/seo/`). When the user
 * navigates client-side between pages those tags would otherwise stay stale —
 * `useSeo()` keeps document.title, the description and the canonical link in
 * sync with the current page so subsequent shares of the URL also work.
 */
import { useEffect } from 'react';

export type PageSeo = {
  title: string;
  description?: string;
  /** Absolute canonical URL for this page. Falls back to `window.location.href`. */
  canonical?: string;
};

function setMeta(name: string, content: string, attr: 'name' | 'property' = 'name'): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel: string, href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/** Set document.title + canonical + og/twitter title & description on mount. */
export function useSeo({ title, description, canonical }: PageSeo): void {
  useEffect(() => {
    document.title = title;
    setMeta('og:title', title, 'property');
    setMeta('twitter:title', title);

    if (description) {
      setMeta('description', description);
      setMeta('og:description', description, 'property');
      setMeta('twitter:description', description);
    }

    const url = canonical ?? (typeof window !== 'undefined' ? window.location.href : '');
    if (url) {
      setLink('canonical', url);
      setMeta('og:url', url, 'property');
    }
  }, [title, description, canonical]);
}
